import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  BarChart3,
  Brain,
  CreditCard,
  Receipt,
  Eraser,
  FileSpreadsheet,
  Folder,
  Home,
  Clock,
  LogOut,
  GraduationCap,
  Settings as SettingsIcon,
  Sparkles,
  Users,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { TrialBanner } from "@/components/subscription/TrialBanner";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { useIsMobile } from "@/hooks/use-mobile";
import { OnboardingWalkthrough } from "@/components/onboarding/OnboardingWalkthrough";
import { useOnboarding, REPLAY_TOUR_EVENT } from "@/hooks/useOnboarding";
import { ProfileMenu } from "@/components/layout/ProfileMenu";

const mainNav = [
  { title: "Dashboard", icon: Home, url: "/" },
  { title: "Activity", icon: Clock, url: "/activity" },
];

const managementNav = [
  { title: "Team Management", icon: Users, url: "/team-management" },
  { title: "Folder Management", icon: Folder, url: "/folder-management" },
  { title: "AI Workflows", icon: Brain, url: "/ai-workflows" },
];

const dataSuiteNav = [
  { title: "Smart Data Entry", icon: Sparkles, url: "/smart-data-entry" },
  { title: "Data Collection", icon: FileSpreadsheet, url: "/data" },
  { title: "Data Cleaning", icon: Eraser, url: "/data-cleaning" },
  { title: "Predictive Models", icon: Brain, url: "/predictive-models" },
  { title: "Dashboards", icon: BarChart3, url: "/dashboards" },
];

const accountNav = [
  { title: "Pricing", icon: CreditCard, url: "/pricing" },
  { title: "Billing", icon: Receipt, url: "/billing" },
  { title: "Settings", icon: SettingsIcon, url: "/settings" },
];

type MainLayoutProps = {
  children: React.ReactNode;
};

import { DEV_MODE } from "@/lib/dev-mode";

const LayoutContent = ({ children }: MainLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { setOpenMobile } = useSidebar();
  const { isOpen: tourOpen, complete: completeTour, replay: replayTour } = useOnboarding();

  useEffect(() => {
    const handler = () => replayTour();
    window.addEventListener(REPLAY_TOUR_EVENT, handler);
    return () => window.removeEventListener(REPLAY_TOUR_EVENT, handler);
  }, [replayTour]);
  const [trialInfo, setTrialInfo] = useState<{
    isInTrial: boolean;
    trialEndDate: string | null;
    hasExpired: boolean;
  }>({
    isInTrial: false,
    trialEndDate: null,
    hasExpired: false
  });

  console.log("Current location:", location.pathname);

  const handleLogout = async () => {
    setOpenMobile(false);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success("Logged out successfully");
      navigate("/welcome");
    } catch (error: any) {
      console.error("Error logging out:", error);
      toast.error(error.message || "Failed to log out");
    }
  };

  const renderMenuItem = (item: {
    title: string;
    icon: React.ElementType;
    url: string;
  }) => (
    <SidebarMenuItem key={item.title}>
      <SidebarMenuButton
        className={`w-full ${
          location.pathname === item.url ? "bg-primary/10 text-primary" : ""
        }`}
        onClick={() => { setOpenMobile(false); navigate(item.url); }}
      >
        <div className="flex items-center gap-3">
          <item.icon className="w-5 h-5" />
          <span>{item.title}</span>
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  useEffect(() => {
    if (!DEV_MODE) checkTrialStatus();
  }, []);

  const checkTrialStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      // Check if user has completed setup and is in trial
      const { data: payments } = await supabase
        .from('payments')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'successful')
        .eq('payment_type', 'one_time')
        .not('business_type', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1);

      if (payments && payments.length > 0) {
        const payment = payments[0];
        const trialEndDate = payment.trial_end_date;
        
        if (trialEndDate) {
          const now = new Date();
          const endDate = new Date(trialEndDate);
          const hasExpired = now > endDate;
          
          // Active subscription now lives in the Paddle-managed `subscriptions` table.
          const { data: subscription } = await supabase
            .from('subscriptions')
            .select('status')
            .eq('user_id', user.id)
            .maybeSingle();

          const hasActiveSubscription = !!subscription &&
            ["active", "trialing", "past_due"].includes(subscription.status);

          setTrialInfo({
            isInTrial: !hasActiveSubscription,
            trialEndDate,
            hasExpired: hasExpired && !hasActiveSubscription
          });

          // If trial expired and no active subscription, redirect to pricing
          if (hasExpired && !hasActiveSubscription && location.pathname !== '/pricing') {
            navigate('/pricing');
          }
        }
      }
    } catch (error) {
      console.error("Error checking trial status:", error);
    }
  };

  return (
      <div className="min-h-screen flex w-full bg-background">
        <Sidebar className="border-r border-sidebar-border bg-sidebar" collapsible="icon">
          <SidebarContent>
            <div className="flex items-center gap-2 px-4 py-4 border-b">
              <img src="/logo.png" alt="Veylo AI" className="w-8 h-8 rounded object-contain" />
              <span className="font-display font-bold text-lg text-foreground">Veylo AI</span>
            </div>
            <SidebarGroup>
              <SidebarGroupLabel>Main</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{mainNav.map(renderMenuItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Management</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{managementNav.map(renderMenuItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Data Suite</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>{dataSuiteNav.map(renderMenuItem)}</SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup>
              <SidebarGroupLabel>Account</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {accountNav.map(renderMenuItem)}
                  <SidebarMenuItem>
                    <SidebarMenuButton className="w-full" onClick={() => { setOpenMobile(false); replayTour(); }}>
                      <div className="flex items-center gap-3">
                        <GraduationCap className="w-5 h-5" />
                        <span>Product Tour</span>
                      </div>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  {/* Logout button */}
                  <SidebarMenuItem>
                  <SidebarMenuButton
                    className="w-full text-destructive hover:bg-destructive/10"
                    onClick={handleLogout}
                  >
                    <div className="flex items-center gap-3">
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </div>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>

        <main className="min-w-0 flex-1">
          <PaymentTestModeBanner />
          {/* Top bar: sidebar trigger on mobile, profile menu everywhere */}
          <header className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b bg-card/90 px-4 md:px-8 backdrop-blur-sm">
            {isMobile && (
              <>
                <SidebarTrigger />
                <img src="/logo.png" alt="Veylo AI" className="w-6 h-6 rounded object-contain" />
                <h1 className="font-semibold">Veylo AI</h1>
              </>
            )}
            <div className="ml-auto">
              <ProfileMenu />
            </div>
          </header>
          
           <div className="mx-auto w-full max-w-7xl p-4 md:p-8">
            {/* Show trial banner if user is in trial or trial has expired */}
            {(trialInfo.isInTrial || trialInfo.hasExpired) && trialInfo.trialEndDate && (
              <TrialBanner 
                trialEndDate={trialInfo.trialEndDate}
              />
            )}
            {children}
            {tourOpen && <OnboardingWalkthrough onComplete={() => completeTour()} />}
          </div>
        </main>
      </div>
  );
};

const MainLayout = ({ children }: MainLayoutProps) => (
  <SidebarProvider>
    <LayoutContent>{children}</LayoutContent>
  </SidebarProvider>
);

export default MainLayout;
